import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import ExportSalesBtn from '../ExportSalesBtn';
import PaginationMePos from '../../pagination/PaginationMePos';
import RenderSellerInfo from '../components/RenderSellerInfo';

const ACTION_COL_KEY = '__actions_placeholder';
const ACTION_COL_WIDTH = '120px';

const GenericStatTable = ({
                            columns = [],
                            rows = [],
                            rowsPerPage = 5,
                            page = 0,
                            onPageChange = (page) => {},
                            noDataMessage = 'No data',
                            tableRowsRaw = [],
                            currency = '',
                            statisticType = undefined,
                          }) => {
  const { t } = useTranslation();

  const totalPages = useMemo(() => Math.max(1, Math.ceil(rows.length / rowsPerPage)), [rows.length, rowsPerPage]);
  const start = useMemo(() => page * rowsPerPage, [page, rowsPerPage]);
  const visibleRows = useMemo(() => rows.slice(start, start + rowsPerPage), [rows, start, rowsPerPage]);

  const showExport = Boolean((Array.isArray(tableRowsRaw) && tableRowsRaw.length) || (Array.isArray(columns) && columns.length));

  const actionCol = {
    key: ACTION_COL_KEY,
    title: '',
    titleKey: '',
    flex: `0 0 ${ACTION_COL_WIDTH}`,
    width: ACTION_COL_WIDTH,
    isActionCol: true
  };

  const columnsForRender = showExport ? [...columns, actionCol] : [...columns];

  // single style function for header and cell
  const colStyle = (col) => ({
    flex: col?.flex ?? '0 0 auto',
    minWidth: col?.width ?? 'auto',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    color: '#1f3354'
  });

  const getHeaderText = (col) => {
    if (!col) return '';
    if (col.title) return col.title;
    if (col.titleKey) {
      const translated = t(col.titleKey);
      return translated === col.titleKey ? col.titleKey : translated;
    }
    return col.key ?? '';
  };

  const renderCell = (cell, col, rowIndex) => {
    if (col?.renderCell) return col.renderCell(cell, { rowIndex });

    // category object: { type: 'category', name, parent }
    if (cell?.type === 'category') {
      const name = cell.name || '';
      const parent = cell.parent || '';
      const title = parent ? `${name} (${parent})` : name;

      return (
        <div
          className="cat-cell"
          title={title}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <span className="fs-6">{name || parent}</span>
          {parent ? <span className="text-info fs-7">({parent})</span> : null}
        </div>
      );
    }

    // employee object
    if (cell?.type === 'employee') {
       return (
        <RenderSellerInfo employee={cell} isAvatar={true} />
      );
    }

    // export-button object
    if (cell?.type === 'export-button') {
      return (
        <div className="d-flex justify-content-end">
          <ExportSalesBtn payload={cell.payload} />
        </div>
      );
    }

    // array/object formats
    if (Array.isArray(cell)) {
      return (
        <>
          <div className="font-16">{cell[0]}</div>
          {cell[1] !== undefined && <div className="opacity-50">{cell[1]}</div>}
        </>
      );
    }
    if (cell && typeof cell === 'object') {
      return (
        <>
          <div className="font-16">{cell.value}</div>
          {cell.subtitle !== undefined && <div className="opacity-50">{cell.subtitle}</div>}
        </>
      );
    }
    return <div className="font-16">{cell}</div>;
  };

  const safeOnPageChange = (newPage) => {
    const safePage = Math.max(0, Math.min(totalPages - 1, newPage));
    if (safePage !== page && onPageChange) onPageChange(safePage);
  };

  if (!rows?.length) {
    return (
      <Col className="my-5">
        <h3 className="col-lg-12 col-5 mb-5 pb-5 text-lg-center text-start">
          {t(noDataMessage)}
        </h3>
      </Col>
    );
  }

  return (
    <Col className="my-5">
      <div className="col-12 table-responsive scrollbar position-relative">
        <div
          className="table d-flex flex-column table-responsive scrollbar position-relative w-100"
          style={{ minWidth: '880px' }}
        >
          <div className="d-flex justify-content-between align-items-center p-3 bg-violet">
            {columnsForRender.map((col) => (
              <div
                key={col.key}
                style={colStyle(col)}
              >
                {col.isActionCol ? (
                  <div className='d-flex justify-content-end w-100 py-0'>
                    <ExportSalesBtn
                      mainColumns={columns}
                      tableRowsRaw={tableRowsRaw}
                      tableRowsArray={rows}
                      currency={currency}
                      filenameBase={statisticType ? statisticType : 'sales_report'}
                    />
                  </div>
                ) : (
                  <div className='w-100 py-0'>{getHeaderText(col)}</div>
                )}
            </div>))}
          </div>
          <div className="d-flex flex-column">
            {visibleRows.map((row, rowIndex ) => (
              <div
                key={start + rowIndex }
                className="d-flex col-12 justify-content-between border-bottom border-secondary-me-pos py-4 px-3"
              >
                {columnsForRender.map((col, colIndex) => {
                  if (col.isActionCol) {
                    return <div key={col.key} className='py-0' style={colStyle(col)} />;
                  }
                  const cell = Array.isArray(row) ? row[colIndex] : row?.[col.key];
                  return (
                    <div key={col.key} className="py-0" style={colStyle(col)}>
                      {renderCell(cell, col, rowIndex)}
                    </div>
                  );
                })}
              </div>))}
          </div>
        </div>
      </div>

      <Row className="mt-2 d-flex align-items-center justify-content-between">
        <Col>
          <PaginationMePos
            totalPages={totalPages}
            currentPage={page}
            onPageChange={(newPage) => safeOnPageChange(newPage)}
            firstItemIndex={start + 1}
            lastItemIndex={Math.min(start + rowsPerPage, rows.length)}
            totalItems={rows.length}
          />
        </Col>
      </Row>
  </Col>);
};

export default GenericStatTable;